/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/11/22, 3:55 PM
 *
 */

import React from 'react';
import moment from 'moment';
import { Member } from 'types/MemberTypes';

import { utcDateToLocal } from 'features/members/utils/utcDateToLocal';

import SunyaLogo from 'assets/img/sunya.svg';

import { URINE_ORDER, URINE_TEST } from 'features/members/components/table/ProfileTestTableRow';

type ProfileTestData = {
  app_slug: string;
  test_name: string;
  test_date: Date;
  temperature: number | null;
  tests: { [p: string]: any }[];
};

type PrintTestComponentProps = {
  test_name: string;
  test: ProfileTestData[];
  member: Member;
};

type PrintProps = React.DetailedHTMLProps<React.AllHTMLAttributes<HTMLDivElement>, HTMLDivElement> &
  PrintTestComponentProps;

const ProfileTestPrint = React.forwardRef<HTMLDivElement, PrintProps>(
  ({ test_name, test, member }, ref) => {
    return (
      <div className="hidden w-full print:flex" ref={ref}>
        <div className="page-header  w-full space-y-4  border-b border-gray-700/20">
          <div className="flex w-full flex-col space-y-8">
            <div className="flex w-full items-end justify-between">
              <div className="flex flex-col gap-4 pt-2">
                <h1 className="text-4xl font-medium tracking-wider text-gray-900">
                  {test_name} Report
                </h1>
                {member && (
                  <div>
                    <h1 className="text-2xl font-medium capitalize tracking-wider text-gray-700">
                      Name: {member.name}
                    </h1>
                    <h1 className="text-2xl font-medium tracking-wider text-gray-700 ">
                      Address: {member.address}
                    </h1>
                    <h1 className="text-2xl font-medium tracking-wider text-gray-700 ">
                      Age: {member.age} years
                    </h1>
                    <h1 className="text-2xl font-medium tracking-wider text-gray-700 ">
                      Gender: {member.gender}
                    </h1>
                    <h1 className="text-2xl font-medium tracking-wider text-gray-700">
                      Date of birth: {moment(member.dob_ad * 1000).format('DD/MM/YYYY')}
                    </h1>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-center self-center">
                <img
                  src={SunyaLogo}
                  width={220}
                  height={80}
                  className="object-contain"
                  alt="Profile Image"
                />{' '}
                <h1 className="pt-4 text-xl font-medium tracking-wider text-slate-900 ">
                  Kathmandu, Nepal
                </h1>
                <h1 className="text-xl font-medium tracking-wider text-slate-900 ">
                  Email: contact@sunya.health
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="page-footer">
          <hr />
          <div className="flex w-full items-center justify-between pt-4">
            <div>
              Printed By Sunya Health ({' '}
              <span className="text-primary-700">{window.location.hostname}</span> ) on{' '}
              {moment(new Date()).format('MMM Do YYYY, h:mm:ss A')}
            </div>

            <span>{member.member_code}</span>
          </div>
        </div>

        <table className="w-full table-fixed">
          <thead className="">
            <tr>
              <td>
                <div className="page-header-space" />
                <span className="text-2xl text-gray-600"> Test Date </span>
              </td>

              <td>
                <div className="page-header-space" />
                <span className="text-2xl text-gray-600"> Temperature </span>
              </td>

              <td>
                <div className="page-header-space" />
                <span className="text-2xl text-gray-600"> Test Result </span>
              </td>
            </tr>
          </thead>

          <tbody className="w-screen divide-y divide-gray-700/20 border-t border-gray-700/20 ">
            {test.map((data, index) => (
              <tr key={index} className="w-full">
                <td className="space-y-2 whitespace-nowrap py-4 align-top text-xl capitalize ">
                  <div className="">
                    <span className="block">
                      {moment(utcDateToLocal(data.test_date)).format('MM/DD/YYYY')}
                    </span>
                    <span className="block">
                      {moment(utcDateToLocal(data.test_date)).format('h:mm A')}
                    </span>
                  </div>
                </td>
                <td className="space-y-2 whitespace-nowrap py-4 align-top text-xl capitalize ">
                  <div className="">
                    <span className="block">
                      {data.temperature} {data.temperature && <>&deg; F</>}
                    </span>
                  </div>
                </td>
                <td className="space-y-2 break-words py-4 text-xl capitalize ">
                  {data.test_name?.toLowerCase() === 'urine'
                    ? URINE_ORDER.map((element, index) => {
                        const unit = data.tests.find((test) =>
                          Object.keys(test).find(
                            (sub_test) => sub_test?.toLowerCase() === element?.toLowerCase()
                          )
                        )?.unit;
                        const value = data.tests.find((test: any) =>
                          Object.keys(test).find(
                            (sub_test) => sub_test?.toLowerCase() === element?.toLowerCase()
                          )
                        )?.[element];

                        return (
                          value?.toLowerCase() !== 'n/a' && (
                            <div key={index} className="flex space-x-2 text-gray-700">
                              <span className="font-medium text-gray-500">
                                {URINE_TEST[element]} :{' '}
                              </span>
                              <span className="font-medium line-clamp-1">{value}</span>
                              <span className="font-medium lowercase line-clamp-1">
                                {unit === 'n/a' ? '' : unit}
                              </span>
                            </div>
                          )
                        );
                      })
                    : data.tests.map(
                        (element, index) =>
                          Object.values(element)?.[0]?.toLowerCase() !== 'n/a' && (
                            <div key={index} className="flex flex-col gap-4 text-gray-700">
                              <div className="flex gap-2">
                                <span className="font-medium text-gray-500">
                                  {Object.keys(element)[0]} :{' '}
                                </span>
                                <span className="font-medium line-clamp-1">
                                  {Object.values(element)[0]}
                                </span>
                                <span className="font-medium lowercase line-clamp-1">
                                  {Object.values(element)[2]?.toLowerCase() === 'n/a'
                                    ? ''
                                    : Object.values(element)[2]?.toLowerCase()}
                                </span>
                              </div>

                              {Object.values(element)[1].length !== 0 && (
                                <div className="flex gap-2">
                                  <span className="font-medium text-gray-500">Notes:</span>
                                  <div className="flex flex-col">
                                    {Object.values(element)[1].map(
                                      (element: any, index: number) => (
                                        <span key={index} className="line-clamp-2">
                                          {' '}
                                          {element}{' '}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                      )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div className="page-footer-space" />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
);

ProfileTestPrint.displayName = 'ProfileTestPrint';

export default ProfileTestPrint;
